/**
 * Reconciliação cliente↔servidor e interpolação (doc 04 §10 A3). Funções puras.
 * - Próprio herói: predição local + reconciliação contra o snapshot autoritativo.
 *   erro ≤ TOL → mantém a predição (sem salto/rubber-band); TOL<erro≤SNAP → lerp suave;
 *   erro > SNAP → snap (desync real).
 * - Outros jogadores: interpolação entre snapshots (buffer ~150ms).
 */
export interface Vec2 {
  x: number;
  y: number;
}

export const RECON_TOL = 8; // px: dentro disso não corrige (evita rubber-band)
export const RECON_SNAP = 64; // px: acima disso, snap direto
export const RECON_LERP = 0.25; // fração de correção por reconciliação (erro médio)

export function reconcile(predicted: Vec2, server: Vec2): Vec2 {
  const dx = server.x - predicted.x;
  const dy = server.y - predicted.y;
  const err = Math.sqrt(dx * dx + dy * dy);
  if (err <= RECON_TOL) return { x: predicted.x, y: predicted.y }; // sem correção visível
  if (err > RECON_SNAP) return { x: server.x, y: server.y }; // desync → snap
  return { x: predicted.x + dx * RECON_LERP, y: predicted.y + dy * RECON_LERP };
}

/** magnitude da correção que reconcile() aplicaria (0 = sem rubber-band) */
export function reconcileCorrection(predicted: Vec2, server: Vec2): number {
  const r = reconcile(predicted, server);
  return Math.sqrt((r.x - predicted.x) ** 2 + (r.y - predicted.y) ** 2);
}

/** interpolação linear de outros jogadores entre dois snapshots (alpha 0..1) */
export function interpolate(a: Vec2, b: Vec2, alpha: number): Vec2 {
  const t = alpha < 0 ? 0 : alpha > 1 ? 1 : alpha;
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
}
